// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Divider,
  Box,
  Typography,
  Paper,
  Snackbar,
  CircularProgress,
} from "@material-ui/core";
import Alert from "../Alert";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import { AppConfig } from "../../Config";
import useHttp from "../utils/http";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Chip from "@material-ui/core/Chip";
import theme from "../../theme";

const useConsultantStyles = makeStyles((theme) => ({
  header: {
    background: "#5f6b7a",
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1.25, 2),
    textAlign: "center",
  },
  listBox: {
    border: "1px solid #c4c4c4",
    borderRadius: 4,
    overflow: "auto",
    background: theme.palette.primary.contrastText,
    justifyContent: "space-around",
  },
  controlRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  inlineBtns: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  chipsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(4),
    marginTop: theme.spacing(1),
  },
  emptyState: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    padding: theme.spacing(1),
  },
  name: {
    fontSize: 14.5,
    fontWeight: 600,
    lineHeight: theme.spacing(0.2),
  },
  email: {
    fontSize: 12,
    marginTop: theme.spacing(0.2),
  },
  resultsBox: {
    border: "1px solid #c4c4c4",
    borderRadius: 4,
    maxHeight: 260,
    overflowY: "auto",
  },
  consultantRow: {
    padding: theme.spacing(1, 1.5),
    borderBottom: "1px solid #eeeeee",
    cursor: "pointer",
    transition: "background 0.2s",
    "&:hover": {
      background: "#f9f9f9",
    },
  },
  selected: {
    background: "#f3f3f3",
    borderLeft: "3px solid #ff7300",
  },
}));

/**
 * Props:
 * open: boolean
 * onClose: () => void
 * onSelect: (payload: { consultant: {name,email}, skills: string[] }) => void
 * engagementId: string,
 * engagementScope: string,
 */
const ConsultantSearchDialog = ({
  open,
  onClose,
  onSelect,
  engagementId,
  engagementScope,
}) => {
  const { handleComponentEncodedRequest, handleRequest } = useHttp();
  const classes = useConsultantStyles();
  const [allSkills, setAllSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(true);
  const [skillInput, setSkillInput] = useState("");
  const [recommendedSkills, setRecommendedSkills] = useState([]);
  const [results, setResults] = useState([]);
  const [scopeEmptyMsg, setScopeEmptyMsg] = useState("");
  const [message, setMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const DEFAULT_ENGAGEMENT_ID = 1234; // This was asked to be added as a temporary fix

  const showMessage = (msg) => {
    setMessage(msg);
    setOpenSnackbar(true);
  };

  const resetDialog = () => {
    setRecommendedSkills([]);
    setLoadingRecommendations(true);
    setSkillInput("");
    setResults([]);
    setHasSearched(false);
    setIsSearching(false);
    setSelectedConsultant(null);
  };

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      resetDialog();
    }
    prevOpenRef.current = open;
  }, [open]);

  const handleAddSkill = () => {
    const s = skillInput.trim();
    if (!s) return;
    if (!recommendedSkills.includes(s)) {
      setRecommendedSkills((prev) => [...prev, s]);
    }
    setSkillInput("");
  };

  const removeSkillAt = (idx) => {
    setRecommendedSkills((prev) => prev.filter((_, i) => i !== idx));
  };

  const getSkills = useCallback(() => {
    const requestUrl = AppConfig.baseUrl + AppConfig.getSkills;

    return new Promise((resolve, reject) => {
      handleComponentEncodedRequest(
        requestUrl,
        "GET",
        null,
        (data) => {
          const sorted = data.sort((a, b) =>
            a.skill.toLowerCase().localeCompare(b.skill.toLowerCase())
          );
          setAllSkills(sorted);
          setLoadingSkills(false);
          resolve(sorted);
        },
        (err) => {
          setLoadingSkills(false);
          reject(err);
        }
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function normalize(s) {
    return (s || "").trim().toLowerCase();
  }

  const getSkillsRecommendations = (skills) => {
    if (engagementScope.trim().length === 0) {
      setRecommendedSkills([]);
      setLoadingRecommendations(false);
      setScopeEmptyMsg(" Engagement scope is empty.");
      return;
    }
    const endPointUrl = AppConfig.baseUrl + AppConfig.skillsRecommendations;
    const skillsList = skills.map((skill) => skill.skill) || [];

    const text = `Following is the task to be carried out:
    ${JSON.stringify(engagementScope)}

Below is the list of available skills:
${JSON.stringify(skillsList, null, 2)}

From the above skills list, suggest the most relevant top 5 skills to perform this task.
Output only the skills in JSON array format without the code block"`;

    const payload = {
      prompt: text,
    };

    handleRequest(
      endPointUrl,
      "POST",
      payload,
      (data) => {
        setRecommendedSkills(data);
        setLoadingRecommendations(false);
        setScopeEmptyMsg("");
      },
      (customErrMsg) => {
        showMessage("Failed to fetch skills recommendations");
        setLoadingRecommendations(false);
      }
    );
  };

  const searchConsultants = () => {
    const endPointUrl = AppConfig.baseUrl + AppConfig.searchConsultants;

    const skillIds = Array.from(
      new Set(
        recommendedSkills
          .map((label) => {
            const hit = allSkills.find(
              (s) => normalize(s.skill) === normalize(label)
            );
            return hit && hit.sys_id ? hit.sys_id : null;
          })
          .filter(Boolean)
      )
    );

    const payload = {
      engagementID: DEFAULT_ENGAGEMENT_ID,
      skill_ids: skillIds,
    };
    setIsSearching(true);
    handleRequest(
      endPointUrl,
      "POST",
      payload,
      (data) => {
        const next = data.result.consultants || [];
        setResults(next);
        setHasSearched(true);
        setIsSearching(false);
      },
      (customErrMsg) => {
        setIsSearching(false);
        setHasSearched(true);
        setResults([]);
        showMessage("Failed to search consultants");
        console.error(customErrMsg);
      }
    );
  };

  useEffect(() => {
    if (!open) return;

    const fetchSkills = async () => {
      try {
        const skills = await getSkills();
        getSkillsRecommendations(skills);
      } catch (err) {
        console.error("Failed to fetch skills:", err);
      }
    };

    fetchSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, getSkills]);

  const handleMessageBarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="consultant-search-title"
    >
      <Paper elevation={0} square className={classes.header}>
        <Typography variant="h4">Consultant Search</Typography>
      </Paper>

      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Suggested Skills
            </Typography>
            <Box
              className={classes.listBox}
              p={1}
              display="flex"
              flexWrap="wrap"
              gridGap={theme.spacing(1)}
              width="100%"
            >
              {loadingRecommendations ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  py={2}
                >
                  <CircularProgress size={24} color="secondary" />
                  <Typography variant="body2" style={{ marginLeft: 10 }}>
                    Fetching recommended skills…
                  </Typography>
                </Box>
              ) : recommendedSkills.length > 0 ? (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gridGap={8}
                  width="100%"
                  p={1}
                >
                  {recommendedSkills.map((s, i) => (
                    <Chip
                      key={`${s}-${i}`}
                      label={s}
                      onDelete={() => removeSkillAt(i)}
                      size="small"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" className={classes.emptyState}>
                  No suggested skills found.{scopeEmptyMsg}
                  <br />
                  Use the dropdown below to add skills.
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <div className={classes.controlRow}>
              <Autocomplete
                options={allSkills}
                getOptionLabel={(option) => option.skill || ""}
                value={allSkills.find((s) => s.skill === skillInput) || null}
                onChange={(_, newValue) => setSkillInput(newValue?.skill || "")}
                disablePortal={false}
                disableClearable
                loading={loadingSkills}
                style={{ width: 450, fontSize: 12 }}
                renderOption={(option) => (
                  <span style={{ fontSize: 14 }}>{option.skill}</span>
                )}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    paddingRight: "0 !important",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder={
                      loadingSkills ? "Loading skills..." : "Select a skill"
                    }
                    inputProps={{
                      ...params.inputProps,
                      style: { fontSize: 14 },
                    }}
                  />
                )}
              />
              <span className={classes.inlineBtns}>
                <IconButton color="primary" onClick={handleAddSkill}>
                  <AddIcon />
                </IconButton>
              </span>
            </div>
            <Divider />
            <Box mt={2}>
              <Button
                variant="contained"
                color="secondary"
                size="medium"
                fullWidth
                onClick={searchConsultants}
                disabled={isSearching || recommendedSkills.length === 0}
              >
                {isSearching ? "Searching..." : "Search consultants"}
              </Button>
            </Box>
          </Grid>
        </Grid>

        {!hasSearched ? (
          <Box
            mt={4}
            display="flex"
            flexDirection="column"
            alignItems="center"
            color="text.secondary"
          >
            <Typography
              variant="body2"
              align="center"
              style={{ maxWidth: 420 }}
            >
              Click <strong>SEARCH CONSULTANTS</strong> to see matches.
            </Typography>
          </Box>
        ) : results.length === 0 ? (
          <Box mt={4} p={2} border="1px dashed #c4c4c4" borderRadius={8}>
            <Typography variant="subtitle2" gutterBottom>
              No consultants found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Try removing a skill, or search with fewer/more general skills.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3} style={{ marginTop: 16 }}>
            <Grid item xs={12} sm={12}>
              <Typography variant="subtitle2" gutterBottom>
                Consultants
              </Typography>

              <Box className={classes.resultsBox}>
                {results.length > 0 ? (
                  results.map((c) => {
                    const isSelected =
                      selectedConsultant &&
                      selectedConsultant.email === c.email;
                    return (
                      <Box
                        key={c.sys_id || c.email}
                        className={`${classes.consultantRow} ${
                          isSelected ? classes.selected : ""
                        }`}
                        onClick={() => {
                          setSelectedConsultant(c);
                        }}
                      >
                        <Typography className={classes.name}>
                          {c.name}
                        </Typography>
                        <Typography className={classes.email}>
                          {c.email}
                        </Typography>

                        <div className={classes.chipsWrap}>
                          {(c.skills || []).map((s) => (
                            <Chip
                              key={s}
                              label={s}
                              size="small"
                              className={classes.chip}
                            />
                          ))}
                        </div>
                      </Box>
                    );
                  })
                ) : (
                  <div className={classes.emptyState}>
                    <Typography variant="body2">
                      No consultants found. Try searching with different skills.
                    </Typography>
                  </div>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        style={{ justifyContent: "flex-end", gap: 8, paddingRight: 23 }}
      >
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={!selectedConsultant}
          onClick={() =>
            onSelect?.({
              consultant: selectedConsultant,
              skills: selectedConsultant?.skills || [],
            })
          }
          autoFocus
        >
          Select
        </Button>
      </DialogActions>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleMessageBarClose}
      >
        <Alert
          onClose={handleMessageBarClose}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ConsultantSearchDialog;
